import React from 'react';
import { NRICData } from '../types/nric';
import { useNotification } from '../hooks/useNotification';

interface ResultsDisplayProps {
  data: NRICData;
  confidence: number;
  rawText: string;
  onReset: () => void;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ 
  data, 
  confidence, 
  rawText, 
  onReset 
}) => {
  const { showSuccess, showError } = useNotification();

  const copyToClipboard = async (text: string, label: string = 'Text') => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copied!', `${label} copied to clipboard`);
    } catch (error) {
      showError('Copy Failed', 'Failed to copy to clipboard');
    }
  };

  const exportAsJSON = () => {
    try {
      const jsonData = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nric_data_${data.nricNumber}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccess('Export Complete', 'JSON file downloaded successfully');
    } catch (error) {
      showError('Export Failed', 'Failed to export JSON file');
    }
  };

  const getConfidenceColor = (conf: number) => {
    if (conf >= 80) return 'text-green-600 bg-green-100';
    if (conf >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfidenceLabel = (conf: number) => {
    if (conf >= 80) return 'High';
    if (conf >= 60) return 'Medium';
    return 'Low';
  };

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="card">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Extracted NRIC Data
            </h2>
            <div className="flex items-center space-x-4">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(confidence)}`}>
                {getConfidenceLabel(confidence)} Confidence ({Math.round(confidence)}%)
              </div>
            </div>
          </div>
          <button
            onClick={onReset}
            className="btn-secondary"
          >
            Process New Image
          </button>
        </div>
      </div>

      {/* Extracted Data */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={data.name}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
                <button
                  onClick={() => copyToClipboard(data.name, 'Name')}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Copy to clipboard"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={data.phone}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
                <button
                  onClick={() => copyToClipboard(data.phone, 'Phone number')}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Copy to clipboard"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                NRIC Number
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={data.nricNumber}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-mono"
                />
                <button
                  onClick={() => copyToClipboard(data.nricNumber, 'NRIC number')}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Copy to clipboard"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={exportAsJSON}
              className="btn-primary"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export JSON
            </button>
            <button
              onClick={() => copyToClipboard(JSON.stringify(data, null, 2), 'All data')}
              className="btn-secondary"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy All Data
            </button>
          </div>
        </div>
      </div>

      {/* Raw OCR Text */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Raw OCR Text</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
            {rawText || 'No text extracted'}
          </pre>
        </div>
        <div className="mt-3">
          <button
            onClick={() => copyToClipboard(rawText, 'Raw OCR text')}
            className="btn-secondary text-sm"
          >
            Copy Raw Text
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;
